import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Images, Loader2, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Compress image to reduce size before upload
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw compressed image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        
        // Estimate compressed size (base64 is ~4/3 the binary size)
        const estimatedSize = Math.round((compressedBase64.length * 3) / 4);
        
        resolve({
          data: compressedBase64,
          originalSize: file.size,
          compressedSize: estimatedSize
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export default function GalleryUploader({ 
  onImagesSelected, 
  maxImages = 6,
  disabled = false 
}) {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const handleButtonClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // Filter only images
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error('Please select image files only');
      return;
    }

    if (imageFiles.length > maxImages) {
      toast.warning(`Only the first ${maxImages} images will be used`);
    }

    setIsProcessing(true);

    try {
      // Compress and convert files to base64
      const imagePromises = imageFiles.slice(0, maxImages).map(async (file) => {
        const stableId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`;
        try {
          const result = await compressImage(file, 1200, 1200, 0.7);
          return {
            id: stableId,
            name: file.name,
            size: result.compressedSize,
            originalSize: result.originalSize,
            data: result.data
          };
        } catch (error) {
          // Fallback to uncompressed if compression fails
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: stableId,
                name: file.name,
                size: file.size,
                originalSize: file.size,
                data: e.target.result
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        }
      });

      const images = await Promise.all(imagePromises);
      setSelectedImages(images);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to process images');
    } finally {
      setIsProcessing(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleConfirmUpload = () => {
    if (selectedImages.length > 0) {
      onImagesSelected(selectedImages.map(img => img.data));
      toast.success(`${selectedImages.length} image${selectedImages.length > 1 ? 's' : ''} added to form`);
    }
    setShowPreviewDialog(false);
    setSelectedImages([]);
  };

  const handleCancel = () => {
    setShowPreviewDialog(false);
    setSelectedImages([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
        data-testid="gallery-input"
      />
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleButtonClick}
        disabled={disabled || isProcessing}
        className="gap-2"
        data-testid="gallery-upload-btn"
      >
        {isProcessing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Images className="h-4 w-4" />
        )}
        Upload from Gallery
      </Button>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Images className="h-5 w-5" />
              Selected Images ({selectedImages.length})
            </DialogTitle>
            <DialogDescription>
              These images will be added to the available slots on this page. 
              {selectedImages.length > maxImages && (
                <span className="text-amber-600 block mt-1">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Only {maxImages} slots available - some images won't be added
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {selectedImages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No images selected
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {selectedImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="relative group rounded-lg overflow-hidden border bg-slate-50"
                  >
                    <img
                      src={image.data}
                      alt={image.name}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleRemoveImage(index)}
                        data-testid={`remove-preview-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 truncate">
                      <span className="font-medium">Slot {index + 1}</span>
                      <span className="text-white/70 ml-2">{formatFileSize(image.size)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmUpload}
              disabled={selectedImages.length === 0}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-gallery-upload-btn"
            >
              <Check className="mr-2 h-4 w-4" />
              Add {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''} to Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
